/**
 * Errors Navigation
 *
 * This file contains functions for navigating between errors in the UI.
 */

import { logError } from 'services/logging/logging';
import { GenericError } from 'support/helpers/errors/errors';

import { InvoiceDiscrepancy } from './types';

// Tables that use virtualization
const VIRTUALIZED_TABLES = ['line_items', 'matched_line_items', 'unmatched_line_items'];

/**
 * Scrolls to and focuses on the specified discrepancy element in the UI.
 */
export const scrollToDiscrepancy = async (
  error: InvoiceDiscrepancy,
  scrollToVirtualizedRow: (tableId: string, rowIndex: number) => void,
): Promise<void> => {
  const selector = `[data-field="${error.id}"]`;
  let element = document.querySelector(selector);

  // If no element it means it is virtualized. We need to scroll the line items container.
  // It's only that table that is virtualized
  if (!element) {
    const table = VIRTUALIZED_TABLES.find((table) => error.id.startsWith(table));
    if (table) {
      element = await getVirtualizedItem(error.id, table, scrollToVirtualizedRow);
    } else {
      console.error('Failed to find element with error: ', { error });
      logError(new GenericError('Discrepancies: Failed to find element with error: ', { error, selector }));
    }
  }

  if (element && 'focus' in element) {
    (element as HTMLInputElement).focus();
    element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
  }
};

/**
 * Gets a virtualized item from a container by its error ID.
 */
const getVirtualizedItem = async (
  errorId: string,
  domContainerId: string,
  scrollToVirtualizedRow: (tableId: string, rowIndex: number) => void,
): Promise<Element | null> => {
  const virtualizedContainer = getVirtualizedContainer(domContainerId);
  if (virtualizedContainer) {
    const index = getItemIndexSection(errorId);
    if (index !== -1) {
      scrollToVirtualizedRow(domContainerId, index);
    }
  }

  return new Promise((resolve) => {
    let numberOfMaximumChecks = 2;
    const intervalId = setInterval(() => {
      const element = document.querySelector(`[data-field="${errorId}"]`);
      if (element) {
        clearInterval(intervalId);
        resolve(element);
      } else if (numberOfMaximumChecks === 0) {
        clearInterval(intervalId);
        resolve(virtualizedContainer);
      } else {
        numberOfMaximumChecks--;
      }
    }, 1);
  });
};

/**
 * Gets the virtualized container element by its ID.
 */
const getVirtualizedContainer = (domContainerId: string): HTMLElement | null => {
  return document.getElementById(domContainerId);
};

/**
 * Gets the item index from an error ID (e.g., "line_items.0.quantity" -> 0).
 */
const getItemIndexSection = (errorId: string): number => {
  // error.id is in the format of line_items.0.quantity
  return parseInt(errorId.split('.')[1]);
};
